import React from 'react';
import { ClipboardList, Calculator, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения таблицы отраслевых нормативов ликвидности
 */
const IndustryStandardsTable = ({ industry, industryTypes, results }) => {
  if (!results || !industry) return null;

  // Находим выбранную отрасль
  const selectedIndustry = industryTypes.find(i => i.id === industry) || {
    id: 'default',
    name: 'По умолчанию',
    currentRatioMin: 2.0,
    quickRatioMin: 1.0,
    cashRatioMin: 0.2
  };

  // Получение статуса для конкретного показателя
  const getRatioStatus = (actual, standard) => {
    if (actual >= standard) {
      return { 
        status: 'good',
        icon: <CheckCircle className="w-5 h-5 text-green-500" />,
        text: 'Соответствует',
        textColor: 'text-green-600 dark:text-green-400'
      };
    } else if (actual >= standard * 0.8) {
      return { 
        status: 'warning',
        icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
        text: 'Близко к нормативу',
        textColor: 'text-yellow-600 dark:text-yellow-400'
      };
    } else {
      return { 
        status: 'danger',
        icon: <XCircle className="w-5 h-5 text-red-500" />,
        text: 'Не соответствует',
        textColor: 'text-red-600 dark:text-red-400'
      };
    }
  };

  // Получаем статус для каждого коэффициента
  const currentRatioStatus = getRatioStatus(results.currentRatio, selectedIndustry.currentRatioMin);
  const quickRatioStatus = getRatioStatus(results.quickRatio, selectedIndustry.quickRatioMin);
  const cashRatioStatus = getRatioStatus(results.cashRatio, selectedIndustry.cashRatioMin);

  // Дополнительные нормативы для других отраслей (для сравнения)
  const otherIndustries = industryTypes
    .filter(i => i.id !== industry)
    .sort((a, b) => a.currentRatioMin - b.currentRatioMin);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ClipboardList className="w-5 h-5 mr-2 text-indigo-500" />
        Отраслевые нормативы ликвидности: {selectedIndustry.name}
      </h3>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Коэффициент
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Фактическое значение
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Норматив для отрасли
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Статус
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Коэффициент текущей ликвидности */}
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                ≥ {formatNumber(selectedIndustry.currentRatioMin, 2)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm flex items-center">
                {currentRatioStatus.icon}
                <span className={`ml-2 ${currentRatioStatus.textColor}`}>
                  {currentRatioStatus.text}
                </span>
              </td>
            </tr>
            
            {/* Коэффициент быстрой ликвидности */}
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                Коэффициент быстрой ликвидности
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                {formatNumber(results.quickRatio, 2)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                ≥ {formatNumber(selectedIndustry.quickRatioMin, 2)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm flex items-center">
                {quickRatioStatus.icon}
                <span className={`ml-2 ${quickRatioStatus.textColor}`}>
                  {quickRatioStatus.text}
                </span>
              </td>
            </tr>
            
            {/* Коэффициент абсолютной ликвидности */}
            <tr>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                Коэффициент абсолютной ликвидности
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                {formatNumber(results.cashRatio, 2)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                ≥ {formatNumber(selectedIndustry.cashRatioMin, 2)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm flex items-center">
                {cashRatioStatus.icon}
                <span className={`ml-2 ${cashRatioStatus.textColor}`}>
                  {cashRatioStatus.text}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Сравнение с другими отраслями */}
      <div className="mt-6">
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">
          Сравнение нормативов с другими отраслями
        </h4>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Отрасль
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Коэфф. текущей ликвидности
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Коэфф. быстрой ликвидности
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Коэфф. абсолютной ликвидности
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Выбранная отрасль (первая в списке) */}
              <tr className="bg-indigo-50 dark:bg-indigo-900/20">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-indigo-700 dark:text-indigo-300">
                  {selectedIndustry.name} (выбрано)
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-indigo-700 dark:text-indigo-300">
                  ≥ {formatNumber(selectedIndustry.currentRatioMin, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-indigo-700 dark:text-indigo-300">
                  ≥ {formatNumber(selectedIndustry.quickRatioMin, 2)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center font-medium text-indigo-700 dark:text-indigo-300">
                  ≥ {formatNumber(selectedIndustry.cashRatioMin, 2)}
                </td>
              </tr>
              
              {/* Другие отрасли */}
              {otherIndustries.slice(0, 5).map((industry) => (
                <tr key={industry.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {industry.name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300">
                    ≥ {formatNumber(industry.currentRatioMin, 2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300">
                    ≥ {formatNumber(industry.quickRatioMin, 2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-300">
                    ≥ {formatNumber(industry.cashRatioMin, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Пояснение */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start">
          <Calculator className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
              О нормативах ликвидности
            </h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Отраслевые нормативы ликвидности отражают специфику оборачиваемости средств в конкретной отрасли. Предприятиям с длительным производственным циклом (например, в строительстве или сельском хозяйстве) требуются более высокие показатели ликвидности, чем компаниям в сфере услуг или торговли, где оборачиваемость средств выше.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryStandardsTable;